import { useShowDialog } from 'App'
import { observer } from 'mobx-react'
import type { ExistingChecklist } from 'models/Checklists/ChecklistModel'
import type { OperationStatus } from 'models/common/OperationStatus'
import { NewVehicleEditCloneModel, VehicleEditClone } from 'models/Vehicles/VehicleModel'
import { isOversightControl } from 'stores/rootStore'
import type { VehicleRUL } from 'models/Vehicles/VehicleRULModel'
import type { VehicleModuleVersion } from 'models/Vehicles/VehicleModuleVersionModel'
import type { VehicleState } from 'models/Vehicles/VehicleStateModel'
import { getLinkForVehiclesPageWithSelectedVehicleAndPart, getLinkForVehiclesPageWithSelectedVehicleDetails, getLinkForVehiclesPageWithSelectedVehicleParts, vehiclesPageWithCreateNewVehicleLink, WithVehiclesRoute, getLinkForVehiclesPageWithSpecificVehicleAndNewPart, getLinkForVehiclesPageWithSpecificVehicleAndEditedPart, getLinkForVehiclesPageWithSelectedVehicleDiscrepancyReports, getLinkForVehiclesPageWithSelectedVehicleAndDiscrepancyReport } from 'router'
import { PrimaryButton, SecondaryButton } from '../Button/Button'
import {  Form, FormProps } from '../Form/Form'
import { SvgIcons } from '../Icon/Icon'
import { MenuButton } from '../MenuButton/MenuButton'
import { VehicleStatusIndicator } from '../StatusIndicator/StatusIndicator'
import { TabLinkContainer, TabLinkWithRoute } from '../TabGroup/TabLink'
import { Row } from '../Table/Table'
import { Heading1, Heading2 } from '../Typography/Typography'
import { VehicleRUL as VehicleRULView } from './VehicleRUL'
import { VehicleDetails } from './VehicleDetails'
import { VehicleVersionsInfo } from './VehicleVersionsInfo'
import { VehiclePartsTable } from './VehiclePartsTable'
import type { ExistingPartInstance, PartInstanceActionLog, PartInstanceEditingClone } from 'models/Vehicles/PartInstanceModel'
import type { ExistingPartNumbers } from 'models/PartNumbers/PartNumbersModel'
import type { ExistingDiscrepancyReport } from 'models/DiscrepancyReports/DiscrepancyReportsModel'
import type { Instance } from 'mobx-state-tree'
import type { CenterSummaryModel } from 'models/CenterSettings/CenterModel'
import { VehicleDiscrepancyReportsTable } from './VehicleDiscrepancyReportsTable'
import { showTransferVehicleDialog } from './TransferVehiclePopup'
interface VehicleFormProps extends WithVehiclesRoute, FormProps {
    editedVehicle: VehicleEditClone
    vehicleState: VehicleState | undefined

    vehicleParts: Array<ExistingPartInstance> | undefined
    vehicleModuleVersionInformation: Array<VehicleModuleVersion> | undefined
    vehicleDiscrepancyReports: Array<ExistingDiscrepancyReport> | undefined

    centerChecklists: Array<ExistingChecklist>
    onSaveVehicle: () => Promise<boolean>
    saveStatus: OperationStatus

    canToggleSelectedVehicleIsParked: boolean
    toggleSelectedVehicleIsParked: () => Promise<void>

    canToggleSelectedVehicleInService: boolean
    toggleSelectedVehicleInService: () => Promise<void>

    selectedPartId: number | null

    partInstanceLogs: Map<string, Array<PartInstanceActionLog>>
    fetchPartInstanceLogStatuses: Map<string, OperationStatus>
    fetchPartInstanceLogs: (partId: number) => Promise<void>
    markPartAsUnAirworthy: (vehicleId: number, partId: number) => Promise<void>
    markPartAsInspected: (vehicleId: number, partId: number) => Promise<void>
    removePartFromVehicle: (vehicleId: number, partId: number) => Promise<void>

    editedPartInstance: PartInstanceEditingClone | null
    partNumbers: Array<ExistingPartNumbers>
    saveEditedPartInstance: () => Promise<boolean>
    saveEditedPartInstanceStatus: OperationStatus
    setEditedPartInstanceFromSerial: (serial: string) => void

    resolveDiscrepancyReport: (reportId: number) => Promise<void>
    assignToDeployment: (vehicleId: number, centerId: number) => Promise<void>
    vehicleRUL: VehicleRUL | undefined
    centers: Array<Instance<typeof CenterSummaryModel>> | null
}
export const VehicleForm = observer(({ 
    editedVehicle, 
    vehicleState, 
    vehicleParts,
    vehicleDiscrepancyReports,
    vehicleModuleVersionInformation,
    saveStatus, 
    onSaveVehicle, 
    currentRoute, 
    centerChecklists, 

    canToggleSelectedVehicleIsParked,
    toggleSelectedVehicleIsParked,

    canToggleSelectedVehicleInService,
    toggleSelectedVehicleInService,

    selectedPartId,

    partInstanceLogs,
    fetchPartInstanceLogStatuses,
    fetchPartInstanceLogs,
    markPartAsUnAirworthy,
    markPartAsInspected,
    removePartFromVehicle,
    editedPartInstance,
    partNumbers,
    saveEditedPartInstance,
    saveEditedPartInstanceStatus,
    setEditedPartInstanceFromSerial,

    resolveDiscrepancyReport,
    assignToDeployment: assignToDeployment,
    vehicleRUL,
    centers,
    ...formProps }: VehicleFormProps) => {
    const showDialog = useShowDialog()
    const isNewVehicle = NewVehicleEditCloneModel.is(editedVehicle)
    return (
        <Form {...{...formProps, isSaving: saveStatus.inProgress}}>
            { !isNewVehicle &&
                <>
                    <Row gap="2em">
                        <Row gap="0.5em" alignItems="center">
                            <Heading1>{ editedVehicle.name }</Heading1>
                            <Heading2>({ editedVehicle.id })</Heading2>
                        </Row>
                        <VehicleStatusIndicator {...{ vehicleStatus: vehicleState?.combinedState }} />
                        <MenuButton {...{
                            ButtonComponent: <SecondaryButton {...{
                                text: isOversightControl() ? 'Vehicle Actions' : 'Vehicle status',
                                buttonStyle: 'thin',
                                endComponent: <SvgIcons.ChevronSvg />,
                                style: { gap: '0'},
                             }} />,
                            style: { marginLeft: 'auto'},
                            menuButtonGroups: [[
                                {
                                     text: vehicleState === undefined ? 'Vehicle service state unknown' : 
                                        vehicleState.isInService ? 'Mark not in service' : 'Mark in service', 
                                     Icon: SvgIcons.CheckCircleSvg,
                                     isDisabled: !canToggleSelectedVehicleInService,
                                     onClick: () => { void toggleSelectedVehicleInService() }
                                }, 
                                { 
                                    text: vehicleState === undefined ? 'Vehicle parking state unknown' : 
                                        vehicleState.isParked ? 'Mark as not parked' : 'Mark as parked', 
                                    Icon: SvgIcons.VehicleParkedSvg, 
                                    isDisabled: !canToggleSelectedVehicleIsParked, 
                                    onClick: () => { void toggleSelectedVehicleIsParked() }
                                },
                                ...(isOversightControl() && editedVehicle.id !== null ? [{
                                    text: 'Transfer Vehicle',
                                    onClick: () => {
                                        if (centers) {
                                            showTransferVehicleDialog({
                                                showDialog,
                                                vehicleId: editedVehicle.id,
                                                vehicleName: editedVehicle.name ?? '',
                                                vehicleDeploymentId: editedVehicle.center_id,
                                                centers,
                                                assignToDeployment: assignToDeployment,
                                            })
                                        }
                                        (document.activeElement as HTMLElement)?.blur()
                                    }
                                },] : [])
                            ]
                            ]
                        }}/>
                    </Row>
                    <TabLinkContainer {...{ orientation: 'horizontal'}}>
                        <TabLinkWithRoute {...{ ...getLinkForVehiclesPageWithSelectedVehicleParts(editedVehicle.id) }}>Parts { vehicleParts !== undefined && `(${vehicleParts.length})`}</TabLinkWithRoute>
                        <TabLinkWithRoute {...{ ...getLinkForVehiclesPageWithSelectedVehicleDetails(editedVehicle.id)}}>Details</TabLinkWithRoute>
                        <TabLinkWithRoute {...{ ...getLinkForVehiclesPageWithSelectedVehicleDiscrepancyReports(editedVehicle.id)}}>Discrepancy Reports { vehicleDiscrepancyReports !== undefined && `(${vehicleDiscrepancyReports.length})` }</TabLinkWithRoute>
                    </TabLinkContainer>
                </>
            }
            {
                ( 
                    currentRoute.name === getLinkForVehiclesPageWithSelectedVehicleDetails(0).name || 
                    currentRoute.name === vehiclesPageWithCreateNewVehicleLink.name
                ) && <>
                    <VehicleDetails {...{ vehicle: editedVehicle, centerChecklists }} /> 
                    <Row style={{justifyContent: 'end'}}>
                        <SecondaryButton {...{ 
                            text: 'discard',
                            isDisabled: !editedVehicle.isDirty,
                            onClick: () => { 
                                editedVehicle.discardChanges(); 
                            }}}/>
                        <PrimaryButton {...{ text: 'save', isDisabled: !editedVehicle.isDirty,
                            onClick: () => {
                                const editedVehicleId = editedVehicle.id
                                void onSaveVehicle().then((saveSuccess: boolean) => {
                                    // in case the save succeeded (ret==true) - we will display the selected vehicle
                                    // in case the save failed (ret==false) - we won't change the screen and will display the vehicle in edit mode
                                    // we assume that the vehicle ID that the user chooses is the ID that is going to be saved in the BE
                                    if (editedVehicleId !== null && saveSuccess){
                                        getLinkForVehiclesPageWithSelectedVehicleDetails(editedVehicleId).replace()
                                    } 
                                })
                                
                                }}} />
                    </Row> 
                    { vehicleRUL != null &&
                        <VehicleRULView {...{ vehicleRUL }} />
                    }
                    { !isNewVehicle && <VehicleVersionsInfo {...{ versionInfo: vehicleModuleVersionInformation }} /> }
                </>

            }
            {
                (
                    currentRoute.name === getLinkForVehiclesPageWithSelectedVehicleParts(0).name ||
                    currentRoute.name === getLinkForVehiclesPageWithSelectedVehicleAndPart(0, 0).name ||
                    currentRoute.name === getLinkForVehiclesPageWithSpecificVehicleAndNewPart(0).name || 
                    currentRoute.name === getLinkForVehiclesPageWithSpecificVehicleAndEditedPart(0, 0).name
                ) && vehicleParts !== undefined && editedVehicle.id !== null &&
                <VehiclePartsTable {...{ 
                    vehicleId: editedVehicle.id,
                    selectedPartId,
                    partInstances: vehicleParts,
                    editedPartInstance,
                    partInstanceLogs,
                    fetchPartInstanceLogStatuses, 
                    fetchPartInstanceLogs,   
                    markPartAsUnAirworthy,
                    markPartAsInspected,
                    removePartFromVehicle,
                    partNumbers,
                    saveEditedPartInstance,
                    saveEditedPartInstanceStatus,
                    setEditedPartInstanceFromSerial,
                }} />
            }
            {
                (
                    currentRoute.name === getLinkForVehiclesPageWithSelectedVehicleDiscrepancyReports(0).name ||
                    currentRoute.name === getLinkForVehiclesPageWithSelectedVehicleAndDiscrepancyReport(0, 0).name
                ) && vehicleDiscrepancyReports && editedVehicle.id !== null && 
                <VehicleDiscrepancyReportsTable {...{
                    vehicleId: editedVehicle.id,
                    vehicleName: editedVehicle.name ?? '',
                    vehicleDiscrepancyReports,
                    selectedDiscrepancyReportId:
                        currentRoute.name === getLinkForVehiclesPageWithSelectedVehicleAndDiscrepancyReport(0, 0).name ? currentRoute.params.discrepancyReportId : null,
                    resolveDiscrepancyReport,
                }}/>
            }
        </Form>
    )
})

